import { z } from 'zod';
import { languageSchema } from '../auth/index';

/**
 * Contracten van de module access: rechten, rollen en roltoewijzing.
 *
 * Een recht heet `<module>.<resource>.<actie>` en wordt door de module zelf
 * geregistreerd. De teksten zijn per taal, want ze staan in het scherm waar de beheerder
 * een rol samenstelt — daar is "directory.person.manage" geen uitleg.
 */

export const localizedTextSchema = z.partialRecord(languageSchema, z.string().min(1).max(200));
export type LocalizedText = z.infer<typeof localizedTextSchema>;

// --- Rechten -------------------------------------------------------------------------

export const policyInfoSchema = z.object({
  key: z.string(),
  module: z.string(),
  /**
   * De leesbare naam van de module, per taal.
   *
   * De sleutel (`directory`, `reach`) blijft erbij staan omdat code erop groepeert; wat de
   * beheerder leest, is dit. Het komt van de module zelf en niet uit een lijst in de client:
   * een nieuwe module zou anders een tweede pakket moeten raken om een naam te krijgen.
   */
  moduleLabel: localizedTextSchema,
  label: localizedTextSchema,
  description: localizedTextSchema,
});
export type PolicyInfo = z.infer<typeof policyInfoSchema>;

/** De registry, gegroepeerd per module: precies zoals het rechtenscherm het toont. */
export const policyCatalogSchema = z.object({
  policies: z.array(policyInfoSchema),
});
export type PolicyCatalog = z.infer<typeof policyCatalogSchema>;

// --- Rollen --------------------------------------------------------------------------

export const roleNameSchema = z.partialRecord(languageSchema, z.string().min(1).max(60));

/**
 * `key` staat alleen op systeemrollen. Daarmee herkent de code "de rol Beheerder" zonder
 * naar de naam te kijken — die is vertaalbaar en door de klant te wijzigen.
 */
export const systemRoleKeySchema = z.enum([
  'admin',
  'hr',
  'communication',
  'teamlead',
  'employee',
  // Uitzendkrachten en aannemers (Stap 31): minder dan een medewerker.
  'temporary',
]);
export type SystemRoleKey = z.infer<typeof systemRoleKeySchema>;

/**
 * De naam van elke systeemrol zoals een fabriek hem krijgt (docs/architecture.md §2.7.4).
 *
 * Hier en niet in `access/internal/domain/system-roles.ts`, sinds ORG-3 (ADR-0099): het startprofiel
 * van een type organisatie in `tenants` geeft andere namen ("Lid" in plaats van "Medewerker"), en
 * Master toont naast elke eigen naam de naam die er zonder zou staan. `tenants` mag `access` niet
 * importeren (ADR-0016); een contract delen ze wel.
 */
export const SYSTEM_ROLE_NAMES: Record<SystemRoleKey, LocalizedText> = {
  admin: { nl: 'Beheerder', fr: 'Administrateur', en: 'Administrator' },
  hr: { nl: 'HR', fr: 'RH', en: 'HR' },
  communication: { nl: 'Communicatie', fr: 'Communication', en: 'Communications' },
  teamlead: { nl: 'Teamleider', fr: "Chef d'équipe", en: 'Team lead' },
  employee: { nl: 'Medewerker', fr: 'Collaborateur', en: 'Employee' },
  temporary: { nl: 'Tijdelijk', fr: 'Temporaire', en: 'Temporary' },
};

/**
 * Waarop een recht slaat (Stap 17, TEAM-1, ADR-0072).
 *
 * Tot TEAM-1 was een recht een ja of een nee. Voor een ploegbaas is dat te grof: wie het bereik
 * van een bericht mag zien, zag daarmee het bereik van élk bericht van het bedrijf. De scope
 * staat bij de **toekenning** en niet bij het recht: dezelfde rol "Teamleider" kan bij de ene
 * klant `own_team` krijgen en bij de andere `tenant`.
 *
 * `site` kwam erbij in Stap 29: een sitebeheerder beheert de mensen van zijn vestiging en ziet de
 * andere niet. Welke vestiging dat is, staat bij de **toekenning van de rol** en niet bij de rol
 * zelf - dezelfde rol "Sitebeheerder" wordt aan Ann voor Gent gegeven en aan Bram voor Kortrijk.
 */
export const policyScopeSchema = z.enum(['tenant', 'site', 'own_team', 'managed_groups']);
export type PolicyScope = z.infer<typeof policyScopeSchema>;

/** Recht → scope. Rechten die er niet in staan, gelden voor het hele bedrijf. */
export const policyScopeMapSchema = z.record(z.string(), policyScopeSchema);

export const roleSchema = z.object({
  id: z.guid(),
  key: systemRoleKeySchema.nullable(),
  name: roleNameSchema,
  isSystem: z.boolean(),
  isDefault: z.boolean(),
  policyKeys: z.array(z.string()),
  /**
   * De afwijkende scopes van deze rol. Alleen de rechten die níet voor het hele bedrijf gelden
   * staan erin; een leeg object betekent dus "alles tenantbreed", niet "niets".
   */
  policyScopes: policyScopeMapSchema,
  memberCount: z.number().int(),
  createdAt: z.string().datetime(),
});
export type Role = z.infer<typeof roleSchema>;

export const roleListSchema = z.object({ roles: z.array(roleSchema) });
export type RoleList = z.infer<typeof roleListSchema>;

export const createRoleRequestSchema = z.object({
  name: roleNameSchema,
  policyKeys: z.array(z.string()).default([]),
  policyScopes: policyScopeMapSchema.default({}),
});
export type CreateRoleRequest = z.infer<typeof createRoleRequestSchema>;

export const updateRoleRequestSchema = z.object({
  name: roleNameSchema.optional(),
  policyKeys: z.array(z.string()).optional(),
  policyScopes: policyScopeMapSchema.optional(),
});
export type UpdateRoleRequest = z.infer<typeof updateRoleRequestSchema>;

// --- Toewijzing ----------------------------------------------------------------------

export const personRolesSchema = z.object({
  personId: z.guid(),
  roleIds: z.array(z.guid()),
});
export type PersonRoles = z.infer<typeof personRolesSchema>;

export const setPersonRolesRequestSchema = z.object({
  roleIds: z.array(z.guid()),
});
export type SetPersonRolesRequest = z.infer<typeof setPersonRolesRequestSchema>;

/**
 * Wat de aangemelde gebruiker mag. De client verbergt hiermee navigatie en knoppen; de
 * server blijft de bron van waarheid en toetst elke aanroep opnieuw.
 */
export const myPermissionsSchema = z.object({
  policyKeys: z.array(z.string()),
  roleIds: z.array(z.guid()),
});
export type MyPermissions = z.infer<typeof myPermissionsSchema>;
